import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { planService } from "../../services/api/planService";
import { toast } from "react-toastify";
import MuiPagination from "../../components/common/MuiPagination";
import { LoadingOverlaySmall } from "../../components/common/LoadingOverlay";
import PlanModal from "../../components/admin/PlanModal";
import DeleteModal from "../../components/common/DeleteModal";
import Searchbox from "../../components/common/Searchbox";
import FilterModal from "../../components/common/FilterModal";
import { FiltersIcon } from "../../components/CustomIcons";

export default function SubscriptionPlans() {
  const [show, setShow] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansData, setPlansData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [search, setSearch] = useState("");

  const [filtersQuery, setFiltersQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const filterFields = [
    {
      name: 'isActive',
      type: 'select',
      label: 'Active',
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
      placeholder: 'All',
    },
    {
      name: 'isDefault',
      type: 'select',
      label: 'Default Plan',
      options: [
        { label: 'Default', value: 'true' },
        { label: 'Not Default', value: 'false' },
      ],
      placeholder: 'All',
    },
    {
      name: 'sortBy',
      type: 'select',
      label: 'Sort by',
      options: [
        { label: 'Newest first', value: 'createdAt:desc' },
        { label: 'Oldest first', value: 'createdAt:asc' },
        { label: 'Price: Low → High', value: 'price:asc' },
        { label: 'Price: High → Low', value: 'price:desc' },
      ],
      placeholder: 'Default',
    },
  ];

  const fetchPlans = useCallback(
    async (p = page, l = limit) => {
      setLoading(true);
      try {
        let q = `plans?page=${p}&limit=${l}`;
        if (search) q += `&search=${encodeURIComponent(search)}`;
        if (filtersQuery) q += `&${filtersQuery.replace(/^\?/, "")}`;

        const response = await planService.list({
          page: p,
          limit: l,
          search: search || undefined,
          ...(filtersQuery ? Object.fromEntries(new URLSearchParams(filtersQuery)) : {}),
        })

        if (response && response.results) {
          const {
            results = [],
            totalResults: respTotal = 0,
            totalPages: respTotalPages = Math.max(1, Math.ceil(respTotal / l)),
            page: respPage = p,
          } = response;

          const formattedData = (results || []).map((plan) => {
            const rawPrice = plan.price ? Number(plan.price) || 0 : 0;
            return {
              id: plan._id,
              planName: plan.name || plan.planName || "N/A",
              price: rawPrice ? `$${rawPrice}` : "$0",
              priceRaw: rawPrice,
              billingCycle: plan.billingCycle || "Monthly",
              status: plan.isActive ? "Active" : "Inactive",
              subscriberCount: plan.subscriberCount || 0,
              description: plan.description || "",
              rawData: plan,
            };
          });

          setPlansData(formattedData || []);
          setTotalResults(Number(respTotal || formattedData.length || 0));
          setTotalPages(Number(respTotalPages));
          setPage(Number(respPage));
        } else {
          toast.error(response?.message || "Failed to fetch plans");
          setPlansData([]);
          setTotalResults(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("An error occurred while fetching plans");
        setPlansData([]);
        setTotalResults(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [limit, page, search, filtersQuery]
  );

  useEffect(() => {
    fetchPlans(page, limit);
  }, [fetchPlans, page, limit, search, filtersQuery]);

  const statusStyles = {
    Active: {
      text: "text-green-700",
      bg: "bg-green-50",
    },
    Inactive: {
      text: "text-red-700",
      bg: "bg-red-50",
    },
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan.rawData);
    setModalMode("edit");
    setShow(true);
  };

  const handleAddNew = () => {
    setSelectedPlan(null);
    setModalMode("add");
    setShow(true);
  };

  const handleModalClose = () => {
    setShow(false);
    setSelectedPlan(null);
  };

  const handleSuccess = () => {
    fetchPlans(page, limit);
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);
    try {
      await planService.delete(deleteId);

      toast.success("Plan deleted successfully");
      setShowDeleteModal(false);
      setDeleteId(null);
      fetchPlans(page, limit);
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("An error occurred while deleting");
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSearch = useCallback((value) => {
    setPage(1);
    setSearch(value);
  }, []);

  const handlePageChange = (_, newPage) => {
    if (
      typeof newPage === "number" &&
      Number.isFinite(newPage) &&
      newPage > 0
    ) {
      setPage(newPage);
      return;
    }
    const maybeIndex =
      (newPage && newPage.selected) ??
      (newPage && newPage.page) ??
      (newPage && newPage.current) ??
      null;
    if (typeof maybeIndex === "number" && Number.isFinite(maybeIndex)) {
      const resolved = maybeIndex + 1;
      if (resolved > 0) setPage(resolved);
    }
  };

  const handleFilterApply = (queryString) => {
    const cleaned = (queryString || "").replace(/^\?/, "");
    setPage(1);
    setFiltersQuery(cleaned);
  };

  return (
    <div className="relative p-6">
      {loading && <LoadingOverlaySmall open={loading} />}

      <div className="flex flex-col md:flex-row items-center py-4 justify-between gap-2 mb-4">
        <h2 className="text-text-dark font-semibold text-xl">Subscription Plans</h2>
        <div className="flex items-center gap-2 flex-col md:flex-row md:w-[700px]">
          <Searchbox
            placeholder="Search plans by name / feature"
            onSearch={handleSearch}
            delay={600}
          />
          <div className="flex justify-between w-full md:w-fit md:justify-end gap-3">
            <button
              onClick={() => setFilterOpen(true)}
              className={`flex items-center gap-2 text-sm font-semibold py-3 px-3 rounded-lg border border-border-light ${
                filtersQuery ? "text-primary" : "text-text-dark"
              }`}
              type="button"
            >
              <span>Filters</span>
              <FiltersIcon
                className={`text-lg ${filtersQuery ? "text-primary" : ""}`}
              />

              {filtersQuery && (
                <Icon
                  icon="mdi:close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersQuery("");
                    setPage(1);
                  }}
                  className="w-5 h-5 text-red-500 cursor-pointer"
                />
              )}
            </button>

            <button
              onClick={handleAddNew}
              className="text-sm font-semibold bg-primary text-white py-3 px-5 rounded-lg whitespace-nowrap hover:bg-primary-dark"
            >
              Add New Plan
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Total Plans</p>
          <p className="text-xl font-semibold text-text-dark mt-2">{totalResults}</p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Total Subscribers</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {plansData.reduce(
              (sum, item) => sum + (item.subscriberCount || 0),
              0
            )}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Monthly Revenue</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            $
            {plansData
              .reduce((sum, item) => {
                const price = Number(item.priceRaw || 0) || 0;
                const subs = Number(item.subscriberCount || 0) || 0;
                return sum + price * subs;
              }, 0)
              .toLocaleString()}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Current Page</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {page} / {Math.ceil(totalResults / limit) || 1}
          </p>
        </div>
      </div>

      {!loading && plansData.length === 0 ? (
        <div className="bg-white border border-border-light rounded-xl p-8 text-center">
          <p className="text-text-secondary">No plans available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {plansData.map((plan) => (
            <div
              key={plan.id}
              className="bg-white border border-border-light rounded-xl p-6 hover:border-primary transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-text-dark font-bold text-xl">
                    {plan.planName}
                  </h3>
                  <p className="text-text-secondary text-sm">{plan.billingCycle}</p>
                </div>
                <div
                  className={`inline-flex gap-2 items-center rounded-full py-1 px-2 ${
                    statusStyles[plan.status]?.text || "text-gray-400"
                  } ${statusStyles[plan.status]?.bg || "bg-gray-100"}`}
                >
                  <Icon icon="mdi:circle" className="text-xs" />
                  {plan.status}
                </div>
              </div>

              <div className="mb-4">
                <span className="text-text-dark font-bold text-3xl">
                  {plan.price}
                </span>
                <span className="text-text-secondary text-sm">
                  /{plan.billingCycle.toLowerCase()}
                </span>
              </div>

              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                {plan.description}
              </p>

              <div className="border-t border-border-light pt-4">
                <p className="text-text-secondary text-xs mb-3">
                  {plan?.subscriberCount} active subscribers
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="flex-1 bg-primary text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors"
                  >
                    Edit Plan
                  </button>
                  <button
                    onClick={() => openDeleteModal(plan.id)}
                    className="bg-red-50 text-red-500 py-2 px-4 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors"
                  >
                    <Icon icon="mdi:delete-outline" className="text-lg" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalResults > limit && (
        <div className="mt-4">
          <MuiPagination
            count={totalResults}
            page={page}
            limit={limit}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </div>
      )}

      <PlanModal
        show={show}
        onHide={handleModalClose}
        modalMode={modalMode}
        planData={selectedPlan}
        onSuccess={handleSuccess}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Plan"
        message="Are you sure you want to delete this plan? This action cannot be undone."
        loading={deleting}
      />

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fields={filterFields}
        onApply={(q) => handleFilterApply(q)}
      />
    </div>
  );
}
